import { useState } from "react";
import { useRevenueSummary, useRevenueStreams, useRevenueEntries } from "@/hooks/use-mock-data";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { DoomsdayGauge } from "@/components/strangelove/DoomsdayGauge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Progress } from "@/components/ui/progress";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from "recharts";
import { Plus, Pencil, Trash2 } from "lucide-react";

const leverageColors: Record<string, string> = {
  high: "#44F2D2",
  medium: "#EBF216",
  low: "#F24B4B",
};

export default function RevenuePage() {
  const { data: summary, refetch: refetchSummary } = useRevenueSummary();
  const { data: streams } = useRevenueStreams();
  const { data: entries, refetch: refetchEntries } = useRevenueEntries();
  const [logOpen, setLogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  return (
    <div className="animate-fade-in">
      {/* Header — glass gradient hero */}
      <div className="glass-card-gradient glow-border-top rounded-none">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <h1 className="font-brand text-4xl md:text-5xl tracking-wide text-center mb-1">DOOMSDAY MACHINE</h1>
          <p className="text-center text-muted-foreground text-sm font-brand tracking-wider">
            {new Date().toLocaleDateString("en-US", { month: "long", year: "numeric" }).toUpperCase()}
          </p>

          {/* Central gauge */}
          <div className="flex flex-col items-center mt-6">
            <DoomsdayGauge current={summary.currentMonth} target={summary.target} size="lg" />
            <div className="mt-4 text-center">
              <span className="text-sm text-muted-foreground">Gap: </span>
              <span className="font-mono font-bold text-electric-yellow">
                ${summary.gap.toLocaleString("en-US", { minimumFractionDigits: 2 })}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-6">
        {/* Revenue Stream Cards */}
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-brand text-2xl tracking-wide">REVENUE STREAMS</h2>
          <Dialog open={logOpen} onOpenChange={setLogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-electric-blue hover:bg-electric-blue/90 text-white text-xs uppercase tracking-wider">
                <Plus className="w-4 h-4 mr-1" /> Log Revenue
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#0C0C0C] border-[rgba(255,255,255,0.08)]">
              <DialogHeader>
                <DialogTitle className="font-brand text-xl tracking-wide">LOG REVENUE</DialogTitle>
              </DialogHeader>
              <form className="space-y-4" onSubmit={async (e) => {
                e.preventDefault();
                const form = e.currentTarget;
                const formData = new FormData(form);
                const streamId = (formData.get("streamId") as string)?.trim();
                const amount = parseFloat(formData.get("amount") as string);
                const date = formData.get("date") as string;
                if (!streamId) { toast.error("Select a revenue stream, Commander."); return; }
                if (!amount || amount <= 0) { toast.error("Amount must be positive."); return; }
                if (!date) { toast.error("Date is required."); return; }
                setSubmitting(true);
                try {
                  await api.post("/api/revenue/entries", {
                    streamId,
                    amount,
                    clientName: (formData.get("clientName") as string) || undefined,
                    date,
                    isRecurring: (formData.get("isRecurring") as string) === "on",
                  });
                  toast.success("Revenue logged. The Doomsday Machine ticks forward, Commander.");
                  setLogOpen(false);
                  form.reset();
                  refetchEntries();
                  refetchSummary();
                } catch (err) {
                  toast.error(err instanceof Error ? err.message : "Failed to log revenue.");
                } finally {
                  setSubmitting(false);
                }
              }}>
                <div>
                  <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Stream</Label>
                  <Select name="streamId">
                    <SelectTrigger className="bg-[rgba(255,255,255,0.05)] border-[rgba(255,255,255,0.08)] mt-1">
                      <SelectValue placeholder="Select stream..." />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0C0C0C] border-[rgba(255,255,255,0.08)]">
                      {streams.map((s) => (
                        <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Client Name</Label>
                  <Input name="clientName" className="bg-[rgba(255,255,255,0.05)] border-[rgba(255,255,255,0.08)] mt-1" placeholder="Client name" />
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Amount</Label>
                  <Input name="amount" className="bg-[rgba(255,255,255,0.05)] border-[rgba(255,255,255,0.08)] mt-1 font-mono" type="number" step="0.01" placeholder="0.00" />
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Date</Label>
                  <Input name="date" className="bg-[rgba(255,255,255,0.05)] border-[rgba(255,255,255,0.08)] mt-1" type="date" defaultValue={new Date().toISOString().split("T")[0]} />
                </div>
                <div className="flex items-center gap-2">
                  <input type="checkbox" id="recurring" name="isRecurring" className="rounded border-[rgba(255,255,255,0.15)]" />
                  <Label htmlFor="recurring" className="text-sm text-muted-foreground">Recurring monthly</Label>
                </div>
                <Button type="submit" disabled={submitting} className="w-full bg-electric-blue hover:bg-electric-blue/90 text-white font-semibold uppercase tracking-wider">
                  {submitting ? "LOGGING..." : "LOG ENTRY"}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
          {streams.map((stream) => {
            const streamRevenue = summary.byStream.find((s) => s.streamId === stream.id)?.total ?? 0;
            const pct = stream.targetMonthly > 0 ? Math.min((streamRevenue / stream.targetMonthly) * 100, 100) : 0;

            return (
              <div
                key={stream.id}
                className={`glass-card rounded-lg p-4 ${!stream.isActive ? "opacity-40" : ""}`}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className={`font-brand text-lg tracking-wide ${!stream.isActive ? "line-through" : ""}`}>
                    {stream.name}
                  </span>
                  <span
                    className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded font-semibold"
                    style={{
                      color: leverageColors[stream.leverageLevel],
                      backgroundColor: `${leverageColors[stream.leverageLevel]}12`,
                    }}
                  >
                    {stream.leverageLevel} leverage
                  </span>
                </div>
                <Progress value={pct} className="h-2 mb-2" />
                <div className="flex items-center justify-between text-xs">
                  <span className="font-mono text-foreground">
                    ${streamRevenue.toLocaleString()} <span className="text-muted-foreground">/ ${stream.targetMonthly.toLocaleString()}</span>
                  </span>
                  {stream.targetClients > 0 && (
                    <span className="text-muted-foreground">
                      Target: {stream.targetClients} clients
                    </span>
                  )}
                </div>
                {stream.avgDealSizeLow > 0 && (
                  <div className="text-[10px] text-muted-foreground mt-1">
                    Deal range: ${stream.avgDealSizeLow.toLocaleString()} - ${stream.avgDealSizeHigh.toLocaleString()}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Monthly Trend Chart */}
        <div className="mb-8">
          <h2 className="font-brand text-2xl tracking-wide mb-4">MONTHLY TREND</h2>
          <div className="glass-card rounded-lg p-4">
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={summary.trend}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                <XAxis dataKey="month" stroke="#555" fontSize={12} />
                <YAxis stroke="#555" fontSize={12} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}K`} />
                <Tooltip
                  contentStyle={{ backgroundColor: "#0C0C0C", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 8, color: "#E8E8E8", fontSize: 12 }}
                  formatter={(value) => [`$${Number(value).toLocaleString()}`, ""]}
                />
                <Line type="monotone" dataKey="revenue" stroke="#4B75F2" strokeWidth={2} dot={{ fill: "#4B75F2", r: 4 }} />
                <ReferenceLine y={83333} stroke="#44F2D2" strokeDasharray="6 3" label={{ value: "Target", fill: "#44F2D2", fontSize: 10 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Revenue Entries Table */}
        <div>
          <h2 className="font-brand text-2xl tracking-wide mb-4">RECENT ENTRIES</h2>
          <div className="glass-card rounded-lg overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="border-[rgba(255,255,255,0.06)] hover:bg-[rgba(255,255,255,0.03)]">
                  <TableHead className="text-[10px] uppercase tracking-wider text-muted-foreground">Date</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-wider text-muted-foreground">Client</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-wider text-muted-foreground">Stream</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-wider text-muted-foreground text-right">Amount</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-wider text-muted-foreground">Recurring</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-wider text-muted-foreground w-20">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {entries.slice(0, 10).map((entry) => (
                  <TableRow key={entry.id} className="border-[rgba(255,255,255,0.06)] hover:bg-[rgba(255,255,255,0.03)]">
                    <TableCell className="font-mono text-xs">{new Date(entry.date).toLocaleDateString()}</TableCell>
                    <TableCell className="text-sm">{entry.clientName}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{entry.streamName}</TableCell>
                    <TableCell className="font-mono text-sm text-right">${entry.amount.toLocaleString("en-US", { minimumFractionDigits: 2 })}</TableCell>
                    <TableCell>
                      {entry.isRecurring && (
                        <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-mint/10 text-mint">
                          recurring
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <button className="text-muted-foreground hover:text-foreground p-1"><Pencil className="w-3 h-3" /></button>
                        <button className="text-muted-foreground hover:text-destructive p-1"><Trash2 className="w-3 h-3" /></button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      </div>
    </div>
  );
}
